// src/hooks/incomeCardConfig.js
//
// Configuration for Step 2 income cards.
// Each entry describes one income_type: its label, which income_subtype
// options to show (if any), whether source_country is asked, and whether
// foreign-tax fields are relevant.
//
// US-specific cards/subtypes are gated by `usOnly: true` — only shown
// when profileFacts.is_us_person is true. This keeps the calculator
// country-agnostic while surfacing US items prominently for US users,
// per the "country-agnostic schema, US-first product" decision.
//
// Subtype values must match IncomeSubtype in calculatorTypes.ts exactly —
// these are sent directly to the classifier.
//
// `category` groups cards into visual sections on Step 2 — see CATEGORY_GROUPS.
// `tablerIcon` is the Tabler outline icon class name (without the ti- prefix).

// ── Category groups ────────────────────────────────────────────────────────
// Drives the section headers in Step 2. Order here is render order.

export const CATEGORY_GROUPS = [
  {
    key: 'work_business',
    label: 'Work & business',
    description: 'Income from employment or running a business',
    tablerIcon: 'ti-briefcase',
  },
  {
    key: 'retirement',
    label: 'Retirement',
    description: 'Pensions and retirement account withdrawals',
    tablerIcon: 'ti-building-bank',
  },
  {
    key: 'investments_property',
    label: 'Investments & property',
    description: 'Investment income, gains, and rental',
    tablerIcon: 'ti-trending-up',
  },
  {
    key: 'other',
    label: 'Other',
    description: 'Anything that doesn\'t fit above',
    tablerIcon: 'ti-dots-circle-horizontal',
  },
]

export const INCOME_CARD_CONFIG = [
  {
    income_type: 'employment',
    category: 'work_business',
    label: 'Employment salary',
    description: 'Salary, wages, or bonuses from an employer.',
    icon: '💼',
    tablerIcon: 'ti-briefcase',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where is the paying company registered?',
    sourceCountryHint: 'Select the country where your employer is legally based — not where you work.',
    subtypeOptions: [
      { value: 'spanish_employer',                label: 'Spanish employer' },
      { value: 'foreign_employer_employee',        label: 'Foreign employer (I am an employee)' },
      { value: 'foreign_employer_owner_operated',  label: 'Foreign employer (I own or control this company)' },
    ],
  },
  {
    income_type: 'self_employment',
    category: 'work_business',
    label: 'Self-employment / autónomo',
    description: 'Revenue and expenses from freelance, consulting, or autónomo activity.',
    icon: '🧾',
    tablerIcon: 'ti-file-invoice',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where are your clients or contracting entity based?',
    sourceCountryHint: 'Select the primary country your invoices or contracts are with.',
    subtypeOptions: null,
    selfEmploymentConfig: {
      expenseFields: [
        { key: 'professional_services',  label: 'Professional services (gestor, legal)' },
        { key: 'software_subscriptions', label: 'Software and subscriptions' },
        { key: 'workspace',              label: 'Workspace (office, coworking, home office)' },
        { key: 'equipment_supplies',     label: 'Equipment and supplies' },
        { key: 'travel_transport',       label: 'Travel and transportation' },
        { key: 'insurance',              label: 'Insurance' },
        { key: 'marketing',              label: 'Marketing and advertising' },
        { key: 'other_expenses',         label: 'Other business expenses' },
      ],
    },
  },
  {
    income_type: 'partnership_business',
    category: 'work_business',
    label: 'Business / partnership income',
    description: 'Pass-through income from a business, LLC, partnership, or company structure.',
    icon: '🏢',
    tablerIcon: 'ti-building',
    // Not US-only — foreign company distributions are equally relevant for non-US users.
    // US-specific subtypes (K-1, S-corp) are labeled clearly within the subtype dropdown.
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where is this business or entity based?',
    sourceCountryHint: null,
    defaultSourceCountry: 'US',
    subtypeOptions: [
      { value: 'us_llc_k1',                            label: 'US LLC (K-1 income)' },
      { value: 'us_s_corp',                             label: 'US S-corp distribution' },
      { value: 'foreign_company_ordinary_distribution', label: "Distribution from foreign company I don't control" },
      { value: 'foreign_company_owner_distribution',    label: 'Distribution from foreign company I own or control' },
      { value: 'other_partnership',                     label: 'Other partnership income' },
    ],
  },
  {
    income_type: 'pension',
    category: 'retirement',
    label: 'Pension income',
    description: 'Regular pension or annuity payments.',
    icon: '🏦',
    tablerIcon: 'ti-building-bank',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Which country pays this pension?',
    sourceCountryHint: null,
    subtypeOptions: [
      { value: 'spanish_public_pension',    label: 'Spanish Seguridad Social' },
      { value: 'spanish_private_pension',   label: 'Spanish private pension plan' },
      { value: 'foreign_private_pension',   label: 'Foreign private pension' },
      { value: 'foreign_public_pension',    label: 'Foreign public (non-government) pension' },
      { value: 'foreign_government_pension', label: 'Foreign government pension' },
    ],
  },
  {
    income_type: 'retirement_distribution',
    category: 'retirement',
    label: 'US retirement account withdrawal',
    description: 'Withdrawals from US tax-advantaged retirement accounts (401k, IRA, Roth, and other US plans).',
    icon: '🏛️',
    tablerIcon: 'ti-landmark',
    // usOnly: true — kept as metadata but no longer used to filter card visibility
    // in Step 2. Cards are shown to all users; the label + subtypes make the
    // US-specific nature clear without requiring an upfront citizenship question.
    usOnly: true,
    askSourceCountry: false,
    defaultSourceCountry: 'US',
    subtypeOptions: [
      { value: 'traditional_401k',      label: 'Traditional 401(k)' },
      { value: 'traditional_ira',       label: 'Traditional IRA' },
      { value: 'roth_401k',             label: 'Roth 401(k)' },
      { value: 'roth_ira',              label: 'Roth IRA' },
      { value: 'other_us_retirement',   label: 'Other US retirement account' },
    ],
  },
  {
    income_type: 'dividends',
    category: 'investments_property',
    label: 'Dividends and interest',
    description: 'Income from brokerage accounts, funds, and bank interest.',
    icon: '📈',
    tablerIcon: 'ti-chart-pie',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where is this brokerage or bank based?',
    sourceCountryHint: null,
    subtypeOptions: null,
  },
  {
    income_type: 'capital_gain',
    category: 'investments_property',
    label: 'Capital gains',
    description: 'Gains from selling investments, property, or crypto.',
    icon: '💹',
    tablerIcon: 'ti-trending-up',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where is the asset or account based?',
    sourceCountryHint: null,
    subtypeOptions: [
      { value: 'stock_sale',                  label: 'Stock or fund sale' },
      { value: 'fund_redemption',             label: 'Fund redemption' },
      { value: 'property_sale_primary',       label: 'Sale of primary residence' },
      { value: 'property_sale_investment',    label: 'Sale of investment property' },
      { value: 'crypto_sale',                 label: 'Crypto sale' },
    ],
  },
  {
    income_type: 'rental',
    category: 'investments_property',
    label: 'Rental income',
    description: 'Income from property you own.',
    icon: '🏠',
    tablerIcon: 'ti-home',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where is the property located?',
    sourceCountryHint: null,
    subtypeOptions: [
      { value: 'spanish_property_long_term',  label: 'Spanish property — long-term rental' },
      { value: 'spanish_property_short_term', label: 'Spanish property — short-term / tourist rental' },
      { value: 'foreign_property',            label: 'Foreign property' },
    ],
    rentalConfig: {
      expenseFields: [
        { key: 'mortgage_interest',   label: 'Mortgage interest' },
        { key: 'insurance',           label: 'Insurance' },
        { key: 'repairs_maintenance', label: 'Repairs / maintenance' },
        { key: 'ibi',                 label: 'IBI / local property tax' },
        { key: 'community_fees',      label: 'Community / HOA fees' },
        { key: 'management_fees',     label: 'Property management fees' },
        { key: 'legal_accounting',    label: 'Legal / accounting fees' },
        { key: 'utilities_owner',     label: 'Utilities paid by you' },
        { key: 'other_expenses',      label: 'Other expenses' },
      ],
      depreciationOptions: [
        { value: 'user_entered',                  label: 'I know my annual depreciation amount' },
        { value: 'estimated_from_purchase_price', label: 'Estimate from purchase price (3%)' },
        { value: 'none',                          label: 'Skip depreciation for now' },
      ],
    },
  },
  {
    income_type: 'other',
    category: 'other',
    label: 'Other income',
    description: "Anything that doesn't fit the categories above.",
    icon: '❓',
    tablerIcon: 'ti-dots-circle-horizontal',
    usOnly: false,
    askSourceCountry: true,
    sourceCountryLabel: 'Where does this income come from?',
    sourceCountryHint: null,
    subtypeOptions: null,
    excludedFromEstimate: true,
    excludedNote: 'Not included in your estimate — for your reference only',
  },
]

// ── Amount basis options ───────────────────────────────────────────────────
// self_employment is intentionally excluded — like rental, it owns its own
// amount_basis via SelfEmploymentExpenseSection (fast-path net vs. itemized
// gross-minus-expenses), not the standard gross/net dropdown. See
// deriveBusinessNet() in IncomeCard.js.

export const AMOUNT_BASIS_ASK_TYPES = new Set(['rental', 'other'])

export const AMOUNT_BASIS_OPTIONS = [
  { value: 'net_entered',   label: 'Net — after expenses' },
  { value: 'gross_entered', label: 'Gross — before expenses' },
]

// ── Period options ─────────────────────────────────────────────────────────

export const PERIOD_OPTIONS = [
  { value: 'annual', label: 'per year' },
  { value: 'monthly', label: 'per month' },
  { value: 'one_time', label: 'one-time' },
]

// ── Currency options ────────────────────────────────────────────────────────
// Limited set for v1 — matches STATIC_EXCHANGE_RATES in irpfStandalone.ts.

export const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'GBP', label: 'GBP (£)' },
]

// ── Activity classification options ────────────────────────────────────────
// Drives Modelo 130's 70%-withholding-exemption applicability — that
// exemption applies to profesional and agricola_ganadera_forestal
// activities only, never to empresarial. See autonomoCuota.ts.

export const ACTIVITY_CLASSIFICATION_OPTIONS = [
  { value: 'profesional',                  label: 'Professional activity (consulting, design, liberal profession)' },
  { value: 'empresarial',                  label: 'Business activity (selling goods, running a shop or agency)' },
  { value: 'agricola_ganadera_forestal',   label: 'Agricultural, livestock, or forestry activity' },
]

// ── Common source countries ────────────────────────────────────────────────
// Short list for the dropdown — "Other" allows free text via a follow-up,
// but for v1 we keep it simple with a fixed list plus Spain/US always first.

export const SOURCE_COUNTRY_OPTIONS = [
  { value: 'ES', label: 'Spain' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'OTHER', label: 'Other' },
]

// ── Empty item factory ──────────────────────────────────────────────────────
// Produces a new IncomeItem with sensible defaults for a given income_type.
// id uses a simple counter-based scheme — fine for client-side session state.

let _itemIdCounter = 0

export function createEmptyIncomeItem(cardConfig, owner = 'self') {
  _itemIdCounter += 1
  const isRental = cardConfig.income_type === 'rental'
  const isSelfEmployment = cardConfig.income_type === 'self_employment'
  return {
    id: `item-${_itemIdCounter}-${Date.now()}`,
    income_type: cardConfig.income_type,
    income_subtype: null,
    label: cardConfig.label,
    owner,

    source_country: cardConfig.defaultSourceCountry || null,
    source_country_status: cardConfig.defaultSourceCountry ? 'provided' : 'unknown',

    amount_period: 'annual',
    // Rental and self-employment start with no basis set — each owns this via
    // its own path toggle (RentalSection / SelfEmploymentExpenseSection).
    // All other AMOUNT_BASIS_ASK_TYPES default to net_entered.
    amount_basis: (isRental || isSelfEmployment) ? null
      : (AMOUNT_BASIS_ASK_TYPES.has(cardConfig.income_type) ? 'net_entered' : 'gross_entered'),
    input_precision: 'exact',

    currency: 'EUR',
    gross_amount_original: 0,
    deductible_expenses_original: 0,
    net_amount_original: 0,
    gross_amount_eur: null,
    net_amount_eur: null,
    exchange_rate_used: null,
    exchange_rate_source: null,

    tax_paid_foreign: 0,
    tax_withheld_spain: 0,
    tax_paid_precision: 'exact',

    // Rental-only metadata — null/false for non-rental items
    rental_expense_path: null,
    rental_expenses: isRental ? {
      mortgage_interest: 0, insurance: 0, repairs_maintenance: 0,
      ibi: 0, community_fees: 0, management_fees: 0,
      legal_accounting: 0, utilities_owner: 0, other_expenses: 0,
    } : null,
    rental_depreciation_source: null,
    rental_depreciation_amount: 0,
    rental_purchase_price: null,

    // Self-employment-only metadata — null for non-self-employment items.
    // See BusinessExpenses in calculatorTypes.ts.
    business_expense_path: null,
    business_expenses: isSelfEmployment ? {
      professional_services: 0, software_subscriptions: 0, workspace: 0,
      equipment_supplies: 0, travel_transport: 0, insurance: 0,
      marketing: 0, other_expenses: 0,
    } : null,
    activity_classification: null,
    activity_started_this_year: null,
    withholding_ratio: null,

    expense_detail_complete: false,
  }
}